import React from 'react';
import { CONTACT_INFO, TRUCK_SERVICES, COMPANY_INFO } from '../utils/constants';
import { SEO_KEYWORDS } from '../utils/seoKeywords';
import PageSEO from '../components/SEO';
import './Services.css';

const Services: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Pickup Truck Rental Services Dubai",
    "description": "Professional pickup truck rental services in Dubai with 1-10 ton capacity trucks, tail lift, and 24/7 availability for home shifting, office moving, and delivery services.",
    "provider": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "telephone": CONTACT_INFO.phone,
      "email": CONTACT_INFO.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": CONTACT_INFO.address,
        "addressLocality": "Dubai",
        "addressCountry": "UAE"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Dubai"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Pickup Truck Rental Services",
      "itemListElement": TRUCK_SERVICES.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        },
        "position": index + 1
      }))
    }
  };

  return (
    <div className="services-page">
        <PageSEO
          title="Pickup Truck Rental Services Dubai | 1-10 Ton & Driver"
          description="Pickup truck rental Dubai: 1–10 ton with tail lift & driver. Home shifting, office moving, furniture & delivery. Same-day, 24/7. Dubai Marina, JVC, Business Bay."
          keywords={SEO_KEYWORDS.services}
        />
        {/* Hero Section */}
        <section className="services-hero">
          <div className="container">
            <h1>Our Pickup Truck Rental Services</h1>
            <p>Professional pickup truck rental services in Dubai with various capacity options and tail lift features</p>
          </div>
        </section>

        {/* Services Overview */}
        <section className="services-overview">
          <div className="container">
            <div className="services-intro">
              <h2>Complete Range of Pickup Truck Services</h2>
              <p>
                We provide comprehensive pickup truck rental services across Dubai with a modern fleet 
                of vehicles ranging from 1 ton to 10 ton capacity. All our trucks are equipped with 
                tail lift for easy loading and unloading, making them perfect for various transportation needs.
              </p>
            </div>

            <div className="services-grid">
              {TRUCK_SERVICES.map((service) => (
                <div key={service.id} id={service.id} className="service-card">
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                    <div className="service-badge">{service.capacity}</div>
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                    
                    <div className="service-features">
                      <h4>Key Features:</h4>
                      <ul>
                        {service.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="service-applications">
                      <h4>Perfect For:</h4>
                      <div className="applications-tags">
                        <span className="tag">Home Shifting</span>
                        <span className="tag">Office Moving</span>
                        <span className="tag">Delivery Services</span>
                        <span className="tag">Goods Transportation</span>
                      </div>
                    </div>

                    <div className="service-actions">
                      <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">
                        Book Now: {CONTACT_INFO.phone}
                      </a>
                      <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">
                        Get Quote
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="service-areas">
          <div className="container">
            <h2>Service Areas in Dubai</h2>
            <p>We provide pickup truck rental services across all major areas of Dubai</p>
            
            <div className="areas-grid">
              <div className="area-category">
                <h3>Central Dubai</h3>
                <ul>
                  <li>Downtown Dubai</li>
                  <li>DIFC</li>
                  <li>Business Bay</li>
                  <li>Jumeirah</li>
                  <li>Marina</li>
                </ul>
              </div>
              <div className="area-category">
                <h3>Northern Emirates</h3>
                <ul>
                  <li>Deira</li>
                  <li>Bur Dubai</li>
                  <li>Karama</li>
                  <li>Satwa</li>
                  <li>Al Quoz</li>
                </ul>
              </div>
              <div className="area-category">
                <h3>New Dubai</h3>
                <ul>
                  <li>Dubai Hills</li>
                  <li>Dubai Sports City</li>
                  <li>JVC</li>
                  <li>JLT</li>
                  <li>Motor City</li>
                </ul>
              </div>
              <div className="area-category">
                <h3>Industrial Areas</h3>
                <ul>
                  <li>Al Quoz Industrial</li>
                  <li>Jebel Ali</li>
                  <li>DIP</li>
                  <li>DIC</li>
                  <li>DWC</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="additional-services">
          <div className="container">
            <h2>Additional Services</h2>
            <div className="additional-grid">
              <div className="additional-item">
                <div className="additional-icon">🏠</div>
                <h3>Home & Office Shifting</h3>
                <p>Complete moving solutions for residential and commercial relocations with professional packing and loading assistance.</p>
              </div>
              <div className="additional-item">
                <div className="additional-icon">📦</div>
                <h3>Delivery Services</h3>
                <p>Reliable delivery services for goods, packages, and materials across Dubai with real-time tracking.</p>
              </div>
              <div className="additional-item">
                <div className="additional-icon">👨‍💼</div>
                <h3>Professional Drivers</h3>
                <p>Experienced and licensed drivers available for all your transportation needs with local area knowledge.</p>
              </div>
              <div className="additional-item">
                <div className="additional-icon">⏰</div>
                <h3>24/7 Availability</h3>
                <p>Round-the-clock service availability to meet your urgent transportation requirements.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Process */}
        <section className="booking-process">
          <div className="container">
            <h2>Simple Booking Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Contact Us</h3>
                <p>Call or email us with your requirements and preferred date/time</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Get Quote</h3>
                <p>Receive a detailed quote based on your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Confirm Booking</h3>
                <p>Confirm your booking and receive confirmation details</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Service Delivery</h3>
                <p>Our professional team arrives on time to provide the service</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Book Your Pickup Truck?</h2>
              <p>Contact us now for reliable and professional pickup truck rental services in Dubai</p>
              <div className="cta-buttons">
                <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call: {CONTACT_INFO.phone}</a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Email Us</a>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default Services;
